import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from "react";
import { User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { ProductSelection } from "@/types/gelato";
import { DeliveryAddress } from "@/types/app";
import { useImageGeneration } from "@/hooks/useImageGeneration";
import { getNanoBananaRatio, type Orientation, type FormatSize } from "@/constants/imageFormats";
import { getDefaultSubStyleId } from "@/constants/artStyles";
import { consumeGuestCreateDraft } from "@/services/GuestCreateDraft";
import { trackFunnelEvent } from "@/services/FunnelAnalytics";

export interface CreateWorkflowState {
  // Step 1 - Artwork
  selectedImage: File | null;
  imagePreview: string | null;
  selectedStyle: string;
  selectedSubStyle: string | null;
  selectedOrientation: Orientation | null;
  restoredGuestDraftNeedsPhoto: boolean;
  generatedImage: string | null;
  generatedResultId: string | null;
  isGenerating: boolean;
  // Step 2 - Support
  selectedSize: FormatSize | null;
  productSelection: ProductSelection | null;
  // Step 3 - Order
  deliveryAddress: DeliveryAddress;
}

interface CreateWorkflowContextType extends CreateWorkflowState {
  user: User | null;
  isAuthResolved: boolean;
  // Step 1 actions
  handleImageUpload: (file: File, preview: string) => void;
  handleImageRemove: () => void;
  clearRestoredGuestDraftNotice: () => void;
  setSelectedStyle: (style: string) => void;
  setSelectedSubStyle: (subStyle: string | null) => void;
  setSelectedOrientation: (orientation: Orientation) => void;
  setGeneratedImage: (
    image: string | null,
    trackGenerationSucceeded?: () => boolean | void | Promise<boolean | void>,
  ) => void;
  generateImage: (
    selectedImage: File | null,
    selectedStyle: string,
    imagePreview: string | null,
    dimensions: string,
    aspectRatio?: string,
    subStyle?: string | null,
  ) => Promise<boolean>;
  // Step 2 actions
  setSelectedSize: (size: FormatSize | null) => void;
  setProductSelection: (selection: ProductSelection | null) => void;
  // Step 3 actions
  setDeliveryAddress: (address: DeliveryAddress) => void;
  // Reset
  resetWorkflow: () => void;
}

const CreateWorkflowContext = createContext<CreateWorkflowContextType | undefined>(undefined);

const initialDeliveryAddress: DeliveryAddress = {
  firstName: "",
  lastName: "",
  email: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  postCode: "",
  country: "FR",
  phone: "",
};

export const CreateWorkflowProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Auth
  const [user, setUser] = useState<User | null>(null);
  const [isAuthResolved, setIsAuthResolved] = useState(false);

  useEffect(() => {
    let isActive = true;
    const checkAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (isActive) setUser(session?.user ?? null);
      } finally {
        if (isActive) setIsAuthResolved(true);
      }
    };
    void checkAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        if (!isActive) return;
        setUser(session?.user ?? null);
        setIsAuthResolved(true);
      }
    );
    return () => {
      isActive = false;
      subscription.unsubscribe();
    };
  }, []);

  // Image generation hook
  const imageGeneration = useImageGeneration(user);

  // Step 1 state
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [selectedStyle, setSelectedStyleState] = useState<string>("");
  const [selectedSubStyle, setSelectedSubStyleState] = useState<string | null>(null);
  const [selectedOrientation, setSelectedOrientationState] = useState<Orientation | null>(null);
  const [restoredGuestDraftNeedsPhoto, setRestoredGuestDraftNeedsPhoto] = useState(false);

  // Step 2 state
  const [selectedSize, setSelectedSizeState] = useState<FormatSize | null>(null);
  const [productSelection, setProductSelection] = useState<ProductSelection | null>(null);

  // Step 3 state
  const [deliveryAddress, setDeliveryAddress] = useState<DeliveryAddress>(initialDeliveryAddress);

  // Restore checkout state after Stripe cancel redirect
  useEffect(() => {
    const raw = sessionStorage.getItem('artify_pending_checkout');
    if (!raw) return;
    try {
      const saved = JSON.parse(raw);
      sessionStorage.removeItem('artify_pending_checkout');
      if (saved.selectedStyle) setSelectedStyleState(saved.selectedStyle);
      if (saved.selectedOrientation) setSelectedOrientationState(saved.selectedOrientation);
      if (saved.selectedSize) setSelectedSizeState(saved.selectedSize);
      if (saved.productSelection) setProductSelection(saved.productSelection);
      if (saved.deliveryAddress) setDeliveryAddress(saved.deliveryAddress);
      if (saved.imagePreview) setImagePreview(saved.imagePreview);
      if (saved.generatedImage) imageGeneration.setGeneratedImage(saved.generatedImage);
    } catch {
      sessionStorage.removeItem('artify_pending_checkout');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Restore the complete guest draft after signup/login, including the local photo.
  useEffect(() => {
    const restoreGuestDraft = async () => {
      const draft = await consumeGuestCreateDraft();
      if (!draft) return;

      if (draft.selectedStyle) setSelectedStyleState(draft.selectedStyle);
      if (draft.selectedSubStyle) setSelectedSubStyleState(draft.selectedSubStyle);
      if (draft.selectedOrientation) setSelectedOrientationState(draft.selectedOrientation);
      if (draft.selectedSize) setSelectedSizeState(draft.selectedSize);

      let imageRestored = false;
      if (draft.selectedImage) {
        const preview = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = () => reject(reader.error ?? new Error("Unable to restore guest image"));
          reader.readAsDataURL(draft.selectedImage!);
        }).catch(() => null);

        if (preview) {
          setSelectedImage(draft.selectedImage);
          setImagePreview(preview);
          imageRestored = true;
        }
      }

      setRestoredGuestDraftNeedsPhoto(draft.hasImageSelection && !imageRestored);
      trackFunnelEvent("guest_precreate_restored", {
        has_style: !!draft.selectedStyle,
        has_sub_style: !!draft.selectedSubStyle,
        has_orientation: !!draft.selectedOrientation,
        has_size: !!draft.selectedSize,
        had_image_selection: draft.hasImageSelection,
        image_restored: imageRestored,
      });
    };

    void restoreGuestDraft();
  }, []);

  // Pre-fill email from authenticated user
  useEffect(() => {
    if (user?.email && !deliveryAddress.email) {
      setDeliveryAddress(prev => ({ ...prev, email: user.email ?? "" }));
    }
  }, [user]);

  // Step 1 actions
  const handleImageUpload = useCallback((file: File, preview: string) => {
    setSelectedImage(file);
    setImagePreview(preview);
    setRestoredGuestDraftNeedsPhoto(false);
    imageGeneration.resetGeneration();
    setProductSelection(null);
  }, [imageGeneration]);

  const handleImageRemove = useCallback(() => {
    setSelectedImage(null);
    setImagePreview(null);
    setSelectedStyleState("");
    setSelectedSubStyleState(null);
    setSelectedOrientationState(null);
    setRestoredGuestDraftNeedsPhoto(false);
    imageGeneration.resetGeneration();
    // Also reset step 2
    setSelectedSizeState(null);
    setProductSelection(null);
  }, [imageGeneration]);

  const clearRestoredGuestDraftNotice = useCallback(() => {
    setRestoredGuestDraftNeedsPhoto(false);
  }, []);

  const setSelectedStyle = useCallback((style: string) => {
    setSelectedStyleState(style);
    // Auto-select default sub-style when style changes
    setSelectedSubStyleState(getDefaultSubStyleId(style));
    imageGeneration.resetGeneration();
    setProductSelection(null);
  }, [imageGeneration]);

  const setSelectedSubStyle = useCallback((subStyle: string | null) => {
    setSelectedSubStyleState(subStyle);
  }, []);

  const setSelectedOrientation = useCallback((orientation: Orientation) => {
    setSelectedOrientationState(orientation);
    // Orientation change resets generated image (ratio changes) and step 2
    imageGeneration.resetGeneration();
    setSelectedSizeState(null);
    setProductSelection(null);
  }, [imageGeneration]);

  const setSelectedSize = useCallback((size: FormatSize | null) => {
    const ratioChanged = !!selectedOrientation && !!selectedSize && !!size
      && getNanoBananaRatio(selectedOrientation, selectedSize) !== getNanoBananaRatio(selectedOrientation, size);

    setSelectedSizeState(size);
    setProductSelection(null);
    if (ratioChanged) imageGeneration.resetGeneration();
  }, [imageGeneration, selectedOrientation, selectedSize]);

  const resetWorkflow = useCallback(() => {
    setSelectedImage(null);
    setImagePreview(null);
    setSelectedStyleState("");
    setSelectedSubStyleState(null);
    setSelectedOrientationState(null);
    setRestoredGuestDraftNeedsPhoto(false);
    imageGeneration.resetGeneration();
    setSelectedSizeState(null);
    setProductSelection(null);
    setDeliveryAddress(initialDeliveryAddress);
  }, [imageGeneration]);

  const value: CreateWorkflowContextType = {
    user,
    isAuthResolved,
    // Step 1
    selectedImage,
    imagePreview,
    selectedStyle,
    selectedSubStyle,
    selectedOrientation,
    restoredGuestDraftNeedsPhoto,
    generatedImage: imageGeneration.generatedImage,
    generatedResultId: imageGeneration.generatedResultId,
    isGenerating: imageGeneration.isGenerating,
    handleImageUpload,
    handleImageRemove,
    clearRestoredGuestDraftNotice,
    setSelectedStyle,
    setSelectedSubStyle,
    setSelectedOrientation,
    setGeneratedImage: imageGeneration.setGeneratedImage,
    generateImage: imageGeneration.generateImage,
    // Step 2
    selectedSize,
    productSelection,
    setSelectedSize,
    setProductSelection,
    // Step 3
    deliveryAddress,
    setDeliveryAddress,
    // Reset
    resetWorkflow,
  };

  return (
    <CreateWorkflowContext.Provider value={value}>
      {children}
    </CreateWorkflowContext.Provider>
  );
};

export const useCreateWorkflowContext = (): CreateWorkflowContextType => {
  const context = useContext(CreateWorkflowContext);
  if (!context) {
    throw new Error("useCreateWorkflowContext must be used within CreateWorkflowProvider");
  }
  return context;
};
